import { ChangeDetectionStrategy, Component, HostBinding, Inject, Input, OnInit } from '@angular/core';

import { BetSlipType } from '@bpos';
import { BetslipConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { CalculatedOdds, Odds, OddsFormatterService } from '@frontend/sports/odds/feature';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import Decimal from 'decimal.js';
import { EpcotConfigService, EpcotModule } from 'packages/sports/web/app/src/common/epcot-config.service';

import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { BetslipType } from '../../../../core/betslip-type';
import { ISucceededSlipResult } from '../../../betplacement/models';
import { TokenPayoutSummary } from '../../models/betslip-result-summary.model';
import { IResultSucceededSlipSummaryViewModel } from '../../models/result-succeeded-slip.model';
import { UsedTokenPayoutSummaryService } from '../../services/used-token-payout-summary.service';

@Component({
    selector: 'bs-digital-result-succeeded-slip',
    templateUrl: './result-succeeded-slip.component.html',
    styleUrls: ['./result-succeeded-slip.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ResultSucceededSlipComponent implements OnInit {
    @Input() slip: ISucceededSlipResult;
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Input() possibleWinningTitle: string;
    @Input() tokenPayoutSummary?: TokenPayoutSummary;
    @Input() isSummaryVisible = true;

    @HostBinding('class') className = 'betslip-result-succeeded-slip';
    summaryViewModel: IResultSucceededSlipSummaryViewModel;
    sportId: number | undefined;
    leagueId: number | undefined;
    title = '';
    headerTitle = '';
    headerSubTitle = '';
    isEpcotEnabled = this.epcotConfigService.isEnabled(EpcotModule.Betslip);

    constructor(
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) private messagesProvider: IBetslipMessagesProviderService,
        private epcotConfigService: EpcotConfigService,
        private interpolationPipe: StringInterpolationPipe,
        public sitecore: Sitecore,
        private betslipConfig: BetslipConfig,
        private usedTokenPayoutSummaryService: UsedTokenPayoutSummaryService,
        private oddsFormatter: OddsFormatterService,
    ) {}

    ngOnInit(): void {
        this.summaryViewModel = this.getSummaryViewModel(this.slip);

        if (this.isEpcotBBMultisEnabled) {
            this.headerTitle = this.sitecore.betslip.messages.BetbuilderComboHeaderTitle;
            this.headerSubTitle = this.interpolationPipe.transform(this.sitecore.betslip.messages.BetbuilderComboHeaderSubTitle, {
                legsCount: this.slip.betCount,
            });
        }
    }

    formattedOdds(odds: Odds | CalculatedOdds | undefined): string {
        return this.oddsFormatter.format(odds, { displayFormat: this.oddsDisplayFormat, isSummary: true });
    }

    get isEpcotBBMultisEnabled(): boolean {
        return this.slip.isMultiBB && this.isEpcotEnabled && this.betslipConfig.allowBetbuilderCombos;
    }

    get isSingleBetPick(): boolean {
        return this.slip.picks.length === 1;
    }

    private getSummaryViewModel(slip: ISucceededSlipResult): IResultSucceededSlipSummaryViewModel {
        const messages = this.messagesProvider.getMessages().result;
        const totalStake = slip.totalStake;
        const stakePerBet = slip.stake;
        const possibleWinningsGross = slip.possibleWinnings?.gross ?? 0;
        const possibleWinningsNet = slip.possibleWinnings?.net ?? 0;
        const taxation = possibleWinningsGross - possibleWinningsNet;
        const viewModel: IResultSucceededSlipSummaryViewModel = {
            betType: this.getBetslipType(slip.betType),
            stake: totalStake,
            stakePerBet,
            possibleWinnings: {
                title: this.possibleWinningTitle,
                value: new Decimal(possibleWinningsGross - taxation),
            },
            rewardTokenInfo: undefined,
        };

        if (slip.picks.length > 1 && slip.totalOdds) {
            // Multi pick bet
            viewModel.odds = {
                title: messages.totalOdds,
                value: slip.totalOdds,
            };
        }

        return this.usedTokenPayoutSummaryService.getViewModelWithTokenInfo(slip, viewModel);
    }

    private getBetslipType(type: BetSlipType): BetslipType {
        if (type === BetSlipType.Single) {
            return BetslipType.Single;
        } else if (type === BetSlipType.Combo) {
            return BetslipType.Combo;
        } else {
            return BetslipType.System;
        }
    }
}
